<template>
  <div class="employee-card-wrapper">
    <v-skeleton-loader v-if="initialLoading" type="image" height="200" class="rounded-xl custom-skeleton mb-2"></v-skeleton-loader>

    <v-card
      v-else
      class="employee-card rounded-xl overflow-hidden elevation-10"
      :class="[cardClass, activeTab === 'planning' ? 'planning-expanded' : '']"
      v-ripple="false">
      <!-- Background Effects -->
      <div class="card-shine"></div>
      <div class="card-mesh"></div>

      <div class="pa-4 relative z-index-2 d-flex flex-column justify-space-between fill-height">
        <!-- Header: Date & Navigation -->
        <div class="d-flex justify-space-between align-center mb-1">
          <v-btn icon small class="glass-btn" @click="previousDate" :disabled="loading">
            <v-icon color="white" small>mdi-chevron-left</v-icon>
          </v-btn>
          <div class="text-center">
            <div class="white--text font-weight-bold text-subtitle-2 text-uppercase letter-spacing-1">
              <span v-if="activeTab === 'planning' && planningView === 'month'">
                {{ $moment(selectedDate).format("MMMM YYYY") }}
              </span>
              <span v-else>
                {{ isToday ? "Heute" : $moment(selectedDate).format("dddd, DD.MM.") }}
              </span>
            </div>
          </div>
          <v-btn icon small class="glass-btn" @click="nextDate" :disabled="loading">
            <v-icon color="white" small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <!-- Tab Switcher -->
        <div v-if="user && user.studio_id && user.studio_id === 'frauenfeld'" class="d-flex justify-center mt-2 mb-2 z-index-2 relative">
          <div class="tab-toggle d-flex w-100 rounded-xl pa-1">
            <div
              class="tab-btn flex-grow-1 text-center py-2 rounded-xl text-caption font-weight-bold transition-fast"
              :class="activeTab === 'tracker' ? 'active-tab shadow-soft' : 'grey--text text--lighten-1'"
              @click="activeTab = 'tracker'">
              <v-icon x-small :color="activeTab === 'tracker' ? 'white' : 'grey'">mdi-clock-outline</v-icon> Zeiterfassung
            </div>
            <div
              class="tab-btn flex-grow-1 text-center py-2 rounded-xl text-caption font-weight-bold transition-fast"
              :class="activeTab === 'planning' ? 'active-tab shadow-soft' : 'grey--text text--lighten-1'"
              @click="activeTab = 'planning'">
              <v-icon x-small :color="activeTab === 'planning' ? 'white' : 'grey'">mdi-calendar-account</v-icon> Meine Planung
            </div>
          </div>
        </div>

        <!-- Main Content (Tracker) -->
        <div
          v-if="activeTab === 'tracker'"
          class="text-center my-1 relative"
          style="min-height: 100px; display: flex; flex-direction: column; justify-content: center">
          <v-progress-circular v-if="loading" indeterminate color="white" size="40" class="absolute-center"></v-progress-circular>

          <div v-else class="fade-in">
            <!-- SPECIAL STATUS: Holiday/Sick/Free if 0h worked and not active -->
            <div v-if="specialStatus && dayWorkTime.minutes === 0 && !currentType" class="d-flex flex-column align-center">
              <v-avatar size="60" :color="specialStatus.color" class="mb-2 elevation-5">
                <v-icon size="32" color="white">{{ specialStatus.icon }}</v-icon>
              </v-avatar>
              <div class="text-h4 font-weight-black white--text text-shadow">{{ specialStatus.text }}</div>
              <div class="text-caption grey--text text--lighten-2 font-weight-bold mt-1">{{ specialStatus.subtext }}</div>
            </div>

            <!-- NORMAL WORK DISPLAY -->
            <div v-else class="d-flex flex-column align-center">
              <div v-if="currentType === 'checkin'" class="timer-dot pulse-dot mb-2"></div>
              <div v-else-if="currentType === 'pause'" class="timer-dot pulse-dot orange mb-2"></div>

              <div class="text-h3 font-weight-black white--text mb-0 text-shadow leading-none">
                {{ dayWorkTime.formattedTime }}<span class="text-h6 opacity-70">h</span>
              </div>

              <!-- Progress Bar if percentage is known -->
              <div v-if="currentMonthPercentage > 0" class="w-100 px-4 mt-2">
                <v-progress-linear
                  :value="workProgress"
                  height="6"
                  rounded
                  :color="progressColor"
                  background-color="rgba(255,255,255,0.15)"></v-progress-linear>
                <div
                  class="d-flex justify-space-between mt-1 text-caption grey--text text--lighten-2 font-weight-bold"
                  style="font-size: 0.65rem !important">
                  <span>
                    SOLL: {{ targetHours }}h
                    <v-chip
                      v-if="isPartialMonth"
                      x-small
                      color="orange darken-3"
                      text-color="white"
                      class="ml-1"
                      style="height: 14px; font-size: 9px; padding: 0 4px">
                      {{ isPartialMonthEntry ? "Eintritt" : "Austritt" }}
                    </v-chip>
                  </span>
                  <span v-if="overtime > 0" class="orange--text">+{{ overtime }}h</span>
                </div>
              </div>

              <!-- Start / End / Pause Info Row -->
              <div class="d-flex align-center justify-center mt-3 gap-3">
                <div class="info-pill d-flex align-center">
                  <v-icon x-small color="grey lighten-1" class="mr-1">mdi-login</v-icon>
                  <span class="text-caption white--text font-weight-bold">{{ dayWorkStart }}</span>
                </div>
                <div class="info-pill d-flex align-center">
                  <v-icon x-small color="grey lighten-1" class="mr-1">mdi-logout</v-icon>
                  <span class="text-caption white--text font-weight-bold">{{ dayWorkEnd === "00:00" && currentType ? "..." : dayWorkEnd }}</span>
                </div>
                <div class="info-pill d-flex align-center" :class="{ 'orange--text': currentType === 'pause' }">
                  <v-icon x-small :color="currentType === 'pause' ? 'orange' : 'grey lighten-1'" class="mr-1">mdi-coffee</v-icon>
                  <span class="text-caption font-weight-bold" :class="currentType === 'pause' ? 'orange--text' : 'white--text'"
                    >{{ dayPauseTime }}h</span
                  >
                </div>
              </div>

              <!-- Action Button (Start) ONLY if showButtons is true and it is today -->
              <div v-if="isToday && showButtons && !currentType" class="mt-4">
                <v-btn
                  rounded
                  color="#00963f"
                  class="px-8 font-weight-bold shadow-glow btn-hover-scale"
                  @click="createTimeEntry('checkin')"
                  :loading="actionLoading">
                  <v-icon left>mdi-power</v-icon> Starten
                </v-btn>
              </div>

              <!-- Correction Button if no buttons allowed -->
              <div v-else-if="!showButtons" class="mt-3">
                <demand-time-entry-edit :selectedDate="selectedDate" @fetchEntries="fetchEntries">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      rounded
                      small
                      color="rgba(255,255,255,0.1)"
                      class="px-4 font-weight-bold white--text border-light"
                      v-bind="attrs"
                      v-on="on">
                      <v-icon left x-small color="orange lighten-1">mdi-pencil</v-icon>
                      Korrektur erfassen
                    </v-btn>
                  </template>
                </demand-time-entry-edit>
              </div>
            </div>
          </div>
        </div>

        <!-- Planning Tab Content -->
        <div
          v-if="activeTab === 'planning'"
          class="planning-view flex-grow-1 d-flex flex-column z-index-2 relative fade-in"
          style="min-height: 300px">
          <div class="d-flex justify-center mb-3">
            <v-btn-toggle
              v-model="planningView"
              mandatory
              rounded
              small
              dark
              background-color="rgba(255,255,255,0.05)"
              color="white"
              style="border: 1px solid rgba(255, 255, 255, 0.1)">
              <v-btn value="day" small class="px-6 text-caption font-weight-bold">Tag</v-btn>
              <v-btn value="month" small class="px-6 text-caption font-weight-bold">Monat</v-btn>
            </v-btn-toggle>
          </div>

          <div v-if="planningLoading" class="flex-grow-1 d-flex justify-center align-center py-4" style="min-height: 250px">
            <v-progress-circular indeterminate color="white" size="30"></v-progress-circular>
          </div>

          <div v-else class="flex-grow-1 overflow-y-auto custom-scrollbar pr-1 mt-1" style="max-height: 320px">
            <!-- Day View -->
            <div v-if="planningView === 'day'" class="fade-in">
              <div
                v-if="!planningData.day.shifts.length && !planningData.day.appointments.length"
                class="text-center mt-4 mb-2 free-day-card pa-4 rounded-lg">
                <div class="free-day-emoji mb-2">🎉</div>
                <div class="white--text text-body-2 font-weight-bold">Frei!</div>
                <div class="grey--text text--lighten-1 text-caption mt-1">An diesem Tag ist nichts geplant</div>
              </div>

              <div v-if="planningData.day.shifts.length > 0" class="mb-4">
                <div class="section-label d-flex align-center mb-2">
                  <v-icon x-small color="grey lighten-1" class="mr-1">mdi-briefcase-clock</v-icon>
                  <span>SCHICHTEN</span>
                </div>
                <div
                  v-for="(shift, i) in planningData.day.shifts"
                  :key="'s' + i"
                  class="planning-item mb-2 d-flex align-center rounded-lg"
                  :style="{ borderLeft: '3px solid ' + (shift.color || '#4CAF50') }">
                  <div class="planning-item-time">
                    <span class="white--text text-caption font-weight-bold">{{ shift.start }}</span>
                    <span class="grey--text text--lighten-1 text-caption mx-1">–</span>
                    <span class="white--text text-caption font-weight-bold">{{ shift.end }}</span>
                  </div>
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-medium" :style="{ color: shift.color || '#81C784' }">{{ shift.title || "Arbeit" }}</span>
                </div>
              </div>

              <!-- Day Summary -->
              <div
                v-if="planningData.day.shifts.length > 0 || planningData.day.appointments.length > 0"
                class="day-summary d-flex align-center justify-center mt-1 mb-3 rounded-lg pa-2">
                <v-icon x-small color="grey lighten-1" class="mr-1">mdi-sigma</v-icon>
                <span class="grey--text text--lighten-1 text-caption">
                  {{ planningData.day.shifts.length }} {{ planningData.day.shifts.length === 1 ? "Schicht" : "Schichten" }}
                  <span v-if="planningData.day.appointments.length > 0">
                    · {{ planningData.day.appointments.length }} {{ planningData.day.appointments.length === 1 ? "Termin" : "Termine" }}
                  </span>
                  <span v-if="dayTotalHours"> · ca. {{ dayTotalHours }}h</span>
                </span>
              </div>

              <div v-if="planningData.day.appointments.length > 0">
                <div class="section-label d-flex align-center mb-2">
                  <v-icon x-small color="grey lighten-1" class="mr-1">mdi-calendar-clock</v-icon>
                  <span>TERMINE</span>
                </div>
                <div
                  v-for="(appt, i) in planningData.day.appointments"
                  :key="'a' + i"
                  class="planning-item mb-2 d-flex align-center rounded-lg"
                  :style="{ borderLeft: '3px solid ' + (appt.color || '#42A5F5') }">
                  <div class="planning-item-time">
                    <span class="white--text text-caption font-weight-bold">{{ appt.time }}</span>
                    <span v-if="appt.endTime" class="grey--text text--lighten-1 text-caption mx-1">–</span>
                    <span v-if="appt.endTime" class="white--text text-caption font-weight-bold">{{ appt.endTime }}</span>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="text-right" style="max-width: 55%">
                    <div class="text-caption font-weight-medium text-truncate" :style="{ color: appt.color || '#90CAF9' }">{{ appt.title }}</div>
                    <div class="text-caption grey--text text--lighten-1" style="font-size: 0.6rem !important; line-height: 1.2">
                      {{ appt.customerName || "Ohne Kunde" }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Month View -->
            <div v-if="planningView === 'month'" class="fade-in">
              <div class="d-flex justify-center mb-3">
                <v-btn
                  small
                  rounded
                  color="rgba(255,255,255,0.08)"
                  class="text-caption font-weight-bold white--text border-light"
                  @click="downloadMonthPlan"
                  :loading="downloadingMonth">
                  <v-icon left small color="white">mdi-download</v-icon> Plan herunterladen
                </v-btn>
              </div>

              <div v-if="!planningData.month.days || !planningData.month.days.length" class="text-center mt-4 mb-2 free-day-card pa-4 rounded-lg">
                <div class="free-day-emoji mb-2">📅</div>
                <div class="white--text text-body-2 font-weight-bold">Noch keine Planung</div>
                <div class="grey--text text--lighten-1 text-caption mt-1">Für diesen Monat ist noch nichts eingetragen</div>
              </div>

              <div
                v-for="(day, i) in planningData.month.days"
                :key="'m' + i"
                class="month-day-card mb-2 rounded-lg pa-2"
                :class="{ 'month-day-today': day.isToday }">
                <div class="d-flex align-center mb-1">
                  <div class="day-dot mr-2" :class="{ 'day-dot-today': day.isToday }"></div>
                  <span class="white--text font-weight-bold text-caption" :class="{ 'today-highlight': day.isToday }">
                    {{ $moment(day.date).format("ddd, DD.MM.") }}
                  </span>
                </div>
                <div v-for="(shift, j) in day.shifts" :key="'ms' + j" class="d-flex align-center ml-4 mt-1">
                  <div class="color-dot mr-2" :style="{ background: shift.color || '#4CAF50' }"></div>
                  <span class="white--text text-caption font-weight-bold mr-2">{{ shift.start }} – {{ shift.end }}</span>
                  <span class="text-caption" :style="{ color: shift.color || '#81C784' }">{{ shift.title || "Schicht" }}</span>
                </div>
                <div v-for="(appt, j) in day.appointments" :key="'ma' + j" class="d-flex align-center ml-4 mt-1">
                  <div class="color-dot mr-2" :style="{ background: appt.color || '#42A5F5' }"></div>
                  <span class="white--text text-caption font-weight-bold mr-2">{{ appt.time }}</span>
                  <span class="text-caption" :style="{ color: appt.color || '#90CAF9' }">{{ appt.title }}</span>
                  <span v-if="appt.customerName" class="text-caption grey--text ml-1" style="font-size: 0.6rem !important"
                    >· {{ appt.customerName }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Planning Footer -->
        <div v-if="activeTab === 'planning'" class="text-center mt-2">
          <v-btn text x-small color="grey lighten-1" @click="goToWorkCalendar"> Zum Kalender <v-icon x-small right>mdi-arrow-right</v-icon> </v-btn>
        </div>

        <!-- Bottom Actions (Tracker) -->
        <div v-if="activeTab === 'tracker'" class="mt-3">
          <!-- Case 1: Active Tracking Controls (Only if showButtons is true) -->
          <v-row v-if="(currentType === 'checkin' || currentType === 'pause') && showButtons" dense>
            <v-col cols="6">
              <v-btn
                block
                small
                :color="currentType === 'pause' ? '#00963f' : 'orange darken-3'"
                class="rounded-lg white--text font-weight-bold shadow-soft"
                @click="createTimeEntry(currentType === 'pause' ? 'pauseend' : 'pause')"
                :loading="actionLoading">
                <v-icon left small>{{ currentType === "pause" ? "mdi-play" : "mdi-coffee" }}</v-icon>
                {{ currentType === "pause" ? "Weiter" : "Pause" }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                small
                color="red darken-3"
                class="rounded-lg white--text font-weight-bold shadow-soft"
                @click="createTimeEntry('checkout')"
                :disabled="currentType === 'pause'"
                :loading="actionLoading">
                <v-icon left small>mdi-stop</v-icon> Stop
              </v-btn>
            </v-col>
          </v-row>

          <!-- Case 2: Shortcuts (Default view when inactive or no controls) -->
          <div v-else class="d-flex justify-space-between align-center px-1">
            <div class="shortcut-item">
              <enter-holidays @fetchEntries="fetchEntries" :holidayEntries="holidayEntries" />
              <span>Ferien</span>
            </div>
            <div class="shortcut-item">
              <enter-free-wishes @fetchEntries="fetchEntries" :freeWishes="freeWishes" />
              <span>Frei</span>
            </div>
            <div class="shortcut-item">
              <enter-shift-wishes />
              <span>Schicht</span>
            </div>
            <div class="shortcut-item" @click="goToTracker">
              <div class="shortcut-btn glass-green">
                <v-icon color="white">mdi-chart-timeline-variant</v-icon>
              </div>
              <span>Tracker</span>
            </div>
          </div>

          <!-- Link back to tracker if active -->
          <div v-if="(currentType === 'checkin' || currentType === 'pause') && showButtons" class="text-center mt-2">
            <v-btn text x-small color="grey lighten-1" @click="goToTracker"> Zur Übersicht <v-icon x-small right>mdi-arrow-right</v-icon> </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import moment from "moment"
import EnterHolidays from "@/views/dashboard/timetracker/EnterHolidays.vue"
import EnterFreeWishes from "@/views/dashboard/timetracker/EnterFreeWishes.vue"
import EnterShiftWishes from "@/views/dashboard/timetracker/EnterShiftWishes.vue"
import DemandTimeEntryEdit from "@/views/dashboard/timetracker/demandTimeEntryEdit.vue"

export default {
  name: "EmployeeCard",
  components: {
    EnterHolidays,
    EnterFreeWishes,
    EnterShiftWishes,
    DemandTimeEntryEdit,
  },
  props: {
    user: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      initialLoading: true,
      loading: false,
      actionLoading: false,
      selectedDate: new Date(),

      // Data from API
      events: [],
      holidayEntries: [],
      sickEntries: [], // NEW
      freeWishes: [],
      currentType: null,
      currentIndex: null,
      interval: null,
      showButtons: false,

      // Tab State
      activeTab: "tracker", // 'tracker' or 'planning'
      planningView: "day", // 'day' or 'month'
      planningLoading: false,
      downloadingMonth: false,
      planningData: {
        day: { shifts: [], appointments: [] },
        month: { days: [] },
      },

      // Studio Info
      employeeStudio: { percentage: 0, entryDate: null, exitDate: null, pensumHistory: [] },
    }
  },
  computed: {
    isToday() {
      return this.$moment(this.selectedDate).isSame(new Date(), "day")
    },
    cardClass() {
      if (this.activeTab === "planning") return "planning-gradient"
      if (this.currentType === "checkin") return "active-gradient"
      if (this.currentType === "pause") return "pause-gradient"
      if (this.specialStatus) return "special-gradient"
      return "default-gradient"
    },
    specialStatus() {
      // Nur relevant, wenn 0 Arbeitszeit und nicht eingecheckt
      if (this.dayWorkTime.minutes > 0 || this.currentType) return null

      // 1. Check Holiday (Bleibt gleich)
      if (this.holidayEntries && this.holidayEntries.length > 0) {
        const isHoliday = this.holidayEntries.some(
          (entry) => entry.status !== "rejected" && entry.dates.some((d) => this.$moment(d.date).isSame(this.selectedDate, "day")),
        )
        if (isHoliday) {
          return { text: "Ferien", subtext: "Genieß die freie Zeit!", icon: "mdi-palm-tree", color: "orange" }
        }
      }

      // 2. Check Absences (Ehemals nur Sick, jetzt generisch)
      if (this.sickEntries && this.sickEntries.length > 0) {
        // Wir suchen den KONKRETEN Eintrag für den heutigen Tag
        const activeEntry = this.sickEntries.find(
          (entry) => entry.sickDays && entry.sickDays.some((d) => this.$moment(d.date).isSame(this.selectedDate, "day")),
        )

        if (activeEntry) {
          // Den Typ aus dem Eintrag holen (Fallback auf 'sick')
          // HINWEIS: Stelle sicher, dass dein API-Objekt ein Feld 'type' oder 'reason' hat, das 'school', 'military' etc. enthält.
          const type = activeEntry.absenceReason || activeEntry.reason || "sick"

          // Konfiguration für die verschiedenen Typen
          const absenceConfig = {
            sick: {
              text: "Krank",
              subtext: "Gute Besserung!",
              icon: "mdi-doctor",
              color: "red",
            },
            school: {
              text: "Schule",
              subtext: "Viel Erfolg beim Lernen!",
              icon: "mdi-school",
              color: "indigo lighten-1",
            },
            pregnancy: {
              text: "Mutterschaft",
              subtext: "Alles Gute!",
              icon: "mdi-baby-carriage",
              color: "pink lighten-2",
            },
            military: {
              text: "Militär",
              subtext: "Im Dienst",
              icon: "mdi-shield-account",
              color: "brown lighten-1",
            },
            holidayFix: {
              text: "Ferien (Nachtrag)",
              subtext: "Nachträgliche Erfassung",
              icon: "mdi-calendar-check",
              color: "orange darken-2",
            },
            other: {
              text: "Abwesend",
              subtext: "Anderer Grund",
              icon: "mdi-help-circle-outline",
              color: "blue-grey",
            },
          }

          // Rückgabe der passenden Config oder Fallback auf 'Krank', falls Typ unbekannt
          return absenceConfig[type] || absenceConfig["sick"]
        }
      }

      // 3. Just Free / No Data (Bleibt gleich)
      if (!this.isToday || !this.showButtons) {
        return { text: "Frei", subtext: "Keine Arbeitszeit erfasst", icon: "mdi-emoticon-happy-outline", color: "blue lighten-2" }
      }

      return null
    },

    // --- Pensum / Target Hours ---
    currentMonthPercentage() {
      if (!this.employeeStudio) return 0
      const targetFormat = this.$moment(this.selectedDate).format("YYYY-MM")
      const history = this.employeeStudio.pensumHistory || []
      const sorted = history.slice().sort((a, b) => b.validFrom.localeCompare(a.validFrom))
      const applicable = sorted.find((h) => h.validFrom <= targetFormat)
      if (applicable) return applicable.percentage
      return this.employeeStudio.percentage || 0
    },
    isPartialMonthEntry() {
      if (!this.employeeStudio || !this.employeeStudio.entryDate) return false
      const entry = this.$moment(this.employeeStudio.entryDate)
      const monthStart = this.$moment(this.selectedDate).startOf("month")
      const monthEnd = this.$moment(this.selectedDate).endOf("month")
      return entry.isAfter(monthStart) && entry.isSameOrBefore(monthEnd)
    },
    isPartialMonthExit() {
      if (!this.employeeStudio || !this.employeeStudio.exitDate) return false
      const exit = this.$moment(this.employeeStudio.exitDate)
      const monthStart = this.$moment(this.selectedDate).startOf("month")
      const monthEnd = this.$moment(this.selectedDate).endOf("month")
      return exit.isBefore(monthEnd) && exit.isSameOrAfter(monthStart)
    },
    isPartialMonth() {
      return this.isPartialMonthEntry || this.isPartialMonthExit
    },
    // Progress Computations
    targetHours() {
      if (!this.currentMonthPercentage) return 0
      return Math.round(8.4 * (this.currentMonthPercentage / 100) * 100) / 100
    },
    workProgress() {
      if (this.targetHours === 0) return 0
      return Math.min((this.dayWorkTime.minutes / 60 / this.targetHours) * 100, 100)
    },
    progressColor() {
      if (this.workProgress >= 100) return "#00e676" // Bright green when done
      return "white"
    },
    overtime() {
      if (this.targetHours === 0) return 0
      const worked = this.dayWorkTime.minutes / 60
      return worked > this.targetHours ? Math.round((worked - this.targetHours) * 100) / 100 : 0
    },

    // Time Computations
    dayWorkStart() {
      if (!this.events || !this.events.length) return "00:00"
      let firstWorkEvent = this.events.find((e) => e.details === "Arbeit")
      if (firstWorkEvent) return this.$moment(firstWorkEvent.start).format("HH:mm")
      else return "00:00"
    },
    dayWorkEnd() {
      if (!this.events || !this.events.length) return "00:00"
      // If currently checked in or paused, we don't have a final end time yet
      if (this.currentType === "checkin" || this.currentType === "pause") return "00:00"

      let lastWorkEvent = [...this.events].reverse().find((e) => e.details === "Arbeit")
      if (lastWorkEvent) return this.$moment(lastWorkEvent.end).format("HH:mm")
      else return "00:00"
    },
    dayPauseTime() {
      if (!this.events || !this.events.length) return "00:00"
      let pauseEvents = this.events.filter((e) => e && e.details === "Pause")
      if (!pauseEvents) return "00:00"

      let totalMinutes = pauseEvents.reduce((acc, cur) => {
        let duration = this.$moment.duration(this.$moment(cur.end).diff(this.$moment(cur.start)))
        return acc + duration.asMinutes()
      }, 0)

      totalMinutes = Math.round(totalMinutes)
      let hours = Math.floor(totalMinutes / 60)
      let minutes = totalMinutes % 60
      return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0")
    },
    dayWorkTime() {
      if (!this.events || !this.events.length) return { formattedTime: "00:00", minutes: 0 }

      let workEvents = this.events.filter(
        (e) =>
          e.details === "Arbeit" ||
          e.details === "Krank" ||
          e.details === "Schule" ||
          e.details === "Schwangerschaft" ||
          e.details === "Ferien (nachtrag)" ||
          e.details === "Anderes" ||
          e.details === "Militär",
      )

      if (!workEvents) return { formattedTime: "00:00", minutes: 0 }

      let totalMinutes = workEvents.reduce((acc, cur) => {
        let duration = this.$moment.duration(this.$moment(cur.end).diff(this.$moment(cur.start)))
        return acc + duration.asMinutes()
      }, 0)

      let start = this.$moment(this.selectedDate).startOf("day")
      let end = this.$moment(this.selectedDate).endOf("day")

      if (this.holidayEntries) {
        this.holidayEntries
          .filter(
            (entry) =>
              entry.status !== "rejected" && entry.dates.find((date) => this.$moment(date.date, "YYYY-MM-DD").isBetween(start, end, null, "[]")),
          )
          .forEach((entry) => {
            entry.dates.forEach((date) => {
              if (this.$moment(date.date, "YYYY-MM-DD").isBetween(start, end, null, "[]")) {
                totalMinutes += date.duration * 60
              }
            })
          })
      }

      totalMinutes = Math.round(totalMinutes)
      let hours = Math.floor(totalMinutes / 60)
      let minutes = totalMinutes % 60
      let formattedTime = hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0")

      return { formattedTime, minutes: totalMinutes }
    },
    dayTotalHours() {
      if (!this.planningData.day.shifts || this.planningData.day.shifts.length === 0) return null
      let totalMinutes = 0
      this.planningData.day.shifts.forEach((s) => {
        if (s.start && s.end && s.start !== "?" && s.end !== "?") {
          const [sh, sm] = s.start.split(":").map(Number)
          const [eh, em] = s.end.split(":").map(Number)
          totalMinutes += eh * 60 + em - (sh * 60 + sm)
        }
      })
      if (totalMinutes <= 0) return null
      return Math.round((totalMinutes / 60) * 10) / 10
    },
    // Planning Watchers
  },
  watch: {
    activeTab(newTab) {
      if (newTab === "planning") {
        this.fetchPlanningData()
      }
    },
    planningView() {
      if (this.activeTab === "planning") {
        this.fetchPlanningData()
      }
    },
    selectedDate() {
      // Refresh planning data when date changes if in planning tab
      if (this.activeTab === "planning") {
        this.fetchPlanningData()
      }
    },
  },
  async created() {
    await this.fetchEntries()
    this.initialLoading = false
  },
  beforeDestroy() {
    this.clearTimer()
  },
  methods: {
    goToTracker() {
      this.$router.push("/dashboard/timetracker")
    },
    goToWorkCalendar() {
      const dateStr = this.$moment(this.selectedDate).format("YYYY-MM-DD")
      this.$router.push({ path: "/work-calendar", query: { date: dateStr } })
    },
    previousDate() {
      if (this.activeTab === "planning" && this.planningView === "month") {
        this.selectedDate = this.$moment(this.selectedDate).subtract(1, "months").startOf("month").toDate()
      } else {
        this.selectedDate = this.$moment(this.selectedDate).subtract(1, "days").toDate()
      }
      this.fetchEntries()
    },
    nextDate() {
      if (this.activeTab === "planning" && this.planningView === "month") {
        this.selectedDate = this.$moment(this.selectedDate).add(1, "months").startOf("month").toDate()
      } else {
        this.selectedDate = this.$moment(this.selectedDate).add(1, "days").toDate()
      }
      this.fetchEntries()
    },

    clearTimer() {
      if (this.interval) clearInterval(this.interval)
      this.interval = null
    },
    startTimer() {
      this.clearTimer()
      if (this.currentIndex !== null) {
        this.interval = setInterval(() => {
          this.renewCurrent()
        }, 15000)
      }
    },
    renewCurrent() {
      if (this.currentIndex !== null && this.events[this.currentIndex]) {
        this.$set(this.events[this.currentIndex], "end", this.$moment().format("YYYY-MM-DD HH:mm"))
      }
    },

    async fetchEntries() {
      this.loading = true
      this.clearTimer()

      let dateStr = this.$moment(this.selectedDate).format("YYYY-MM-DD")
      let dates = { start: dateStr, end: dateStr }

      try {
        let entries = await api.fetchTimetrackerCalendarEntries(dates, true)

        let allTimeEntries = entries.mappedTimeEntries || []
        this.holidayEntries = entries.holidayEntries || []
        this.sickEntries = entries.sickEntries || [] // Populate Sick Entries
        this.freeWishes = entries.freeWishes || []
        this.employeeStudio = entries.studio || { percentage: 0 }
        this.showButtons = entries.showButtons

        let currentFound = false

        allTimeEntries = allTimeEntries.map((e, index) => {
          if (e.end === "current") {
            currentFound = true
            this.currentType = e.type
            this.currentIndex = index
            e.end = this.$moment().format("YYYY-MM-DD HH:mm")
          }
          return e
        })

        if (!currentFound) {
          this.currentType = null
          this.currentIndex = null
        }

        this.events = allTimeEntries
        this.startTimer()
      } catch (e) {
        console.error("Error fetching employee entries", e)
      } finally {
        this.loading = false
      }
    },

    async createTimeEntry(type) {
      this.actionLoading = true
      let entry = {
        date: this.$moment().format("YYYY-MM-DD HH:mm"),
        type: type,
        reason: null,
        smoke: false,
      }

      try {
        let status = await api.createTimetrackerEntryEmployee(entry)
        if (status) {
          await this.fetchEntries()
        }
      } catch (e) {
        this.$toast.error("Fehler bei der Zeitbuchung")
      } finally {
        this.actionLoading = false
      }
    },

    // --- Planning Tab Methods ---
    async fetchPlanningData() {
      this.planningLoading = true
      try {
        if (this.planningView === "day") {
          let dateStr = this.$moment(this.selectedDate).format("YYYY-MM-DD")
          let data = await api.shiftManagement.fetchEmployeeScheduleDaily(dateStr)
          this.planningData.day = data || { shifts: [], appointments: [] }
        } else {
          let monthStr = this.$moment(this.selectedDate).format("YYYY-MM")
          let data = await api.shiftManagement.fetchEmployeeScheduleMonthly(monthStr)
          this.planningData.month = data || { days: [] }
        }
      } catch (e) {
        console.error("Error fetching planning data", e)
        if (this.planningView === "day") {
          this.planningData.day = { shifts: [], appointments: [] }
        } else {
          this.planningData.month = { days: [] }
        }
      } finally {
        this.planningLoading = false
      }
    },

    async downloadMonthPlan() {
      this.downloadingMonth = true
      try {
        let monthStr = this.$moment(this.selectedDate).format("YYYY-MM")
        const blob = await api.shiftManagement.downloadEmployeeSchedule(monthStr)
        if (blob) {
          const { saveAs } = require("file-saver")
          const fileName = blob.type === "application/pdf" ? `Schichtplan_${monthStr}.pdf` : `Schichtplan_${monthStr}.txt`
          saveAs(blob, fileName)
          this.$toast.success("Plan erfolgreich heruntergeladen")
        } else {
          this.$toast.error("Download fehlgeschlagen")
        }
      } catch (e) {
        console.error("Download failed", e)
        this.$toast.error("Download fehlgeschlagen")
      } finally {
        this.downloadingMonth = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* CARD STYLES */
.employee-card {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-height: 200px;
}
.planning-expanded {
  min-height: 420px !important;
}

/* GRADIENTS */
.default-gradient {
  background: linear-gradient(135deg, #1e3c2f 0%, #0d1f16 100%);
  border: 1px solid rgba(82, 174, 50, 0.3);
}
.active-gradient {
  background: linear-gradient(135deg, #004d20 0%, #002911 100%);
  border: 1px solid rgba(0, 150, 63, 0.5);
  box-shadow: 0 0 20px rgba(0, 150, 63, 0.2) !important;
}
.pause-gradient {
  background: linear-gradient(135deg, #4a2c00 0%, #291800 100%);
  border: 1px solid rgba(255, 152, 0, 0.3);
}
.special-gradient {
  background: linear-gradient(135deg, #1a237e 0%, #000000 100%);
  border: 1px solid rgba(63, 81, 181, 0.3);
}
.planning-gradient {
  background: linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%);
  border: 1px solid rgba(100, 120, 180, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4) !important;
}

/* UTILS */
.glass-btn {
  background: rgba(255, 255, 255, 0.1);
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  &[disabled] {
    opacity: 0.3;
  }
}
.glass-green {
  background: linear-gradient(135deg, rgba(82, 174, 50, 0.7), rgba(46, 102, 28, 0.7));
}

.card-shine {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}
.card-mesh {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.5;
  z-index: 0;
}
.z-index-2 {
  position: relative;
  z-index: 2;
}
.relative {
  position: relative;
}
.absolute-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.fill-height {
  height: 100%;
}

/* TABS */
.tab-toggle {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}
.tab-btn {
  cursor: pointer;
  opacity: 0.7;
}
.active-tab {
  background: rgba(255, 255, 255, 0.15);
  color: white !important;
  opacity: 1;
}
.transition-fast {
  transition: all 0.2s ease;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* PLANNING VIEW */
.section-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 1.5px;
  text-transform: uppercase;
}
.planning-item {
  background: rgba(255, 255, 255, 0.05);
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}
.planning-item-time {
  white-space: nowrap;
}
.month-day-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.2s ease;
}
.month-day-today {
  background: rgba(100, 140, 255, 0.08) !important;
  border-color: rgba(100, 140, 255, 0.2) !important;
}
.day-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
}
.day-dot-today {
  background: #64b5f6;
  box-shadow: 0 0 6px rgba(100, 181, 246, 0.5);
}
.today-highlight {
  color: #64b5f6 !important;
}
.color-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* FREE DAY & SUMMARY */
.free-day-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
}
.free-day-emoji {
  font-size: 2.5rem;
  line-height: 1;
  animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.day-summary {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

/* TEXT & BADGES */
.letter-spacing-1 {
  letter-spacing: 1px;
}
.leading-none {
  line-height: 1;
}
.text-shadow {
  text-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
}
.backdrop-blur {
  backdrop-filter: blur(4px);
}

/* ANIMATIONS */
.fade-in {
  animation: fadeIn 0.4s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #00e676;
  border-radius: 50%;
  box-shadow: 0 0 10px #00e676;
  animation: blink 2s infinite;
  &.orange {
    background-color: #ff9800;
    box-shadow: 0 0 10px #ff9800;
  }
}
@keyframes blink {
  50% {
    opacity: 0.5;
  }
}

.btn-hover-scale:hover {
  transform: scale(1.05);
  transition: 0.2s;
}
.shadow-glow {
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.4);
}
.shadow-soft {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

/* SHORTCUTS */
.shortcut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
  &:active {
    transform: scale(0.95);
  }

  span {
    font-size: 0.65rem;
    font-weight: 700;
    color: #ddd;
    text-transform: uppercase;
    margin-top: 4px;
    letter-spacing: 0.5px;
  }
}
.shortcut-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

/* INFO PILLS */
.info-pill {
  background: rgba(255, 255, 255, 0.08);
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.gap-3 {
  gap: 12px;
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.15);
}

/* OVERRIDES */
::v-deep .v-btn--icon.v-size--large {
  height: 40px;
  width: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
::v-deep .v-btn--icon.v-size--large .v-icon {
  font-size: 20px;
}
</style>
